import instance from "api/cmsapi";
import {
  flattenDistrictsWithRegions,
  buildRegionFilterOptions,
} from "../apr-dashboard/aprWorkflowStats";
import {
  buildCommitteePetitionTableId,
  buildDistrictPetitionTableId,
  buildPetitionDecisionHtml,
  derivePetitionRowStatus,
  DPAT_PETITION_TYPE,
  getPetitionStatusMeta,
  isPetitionEligibleAssessmentStatus,
  normalizePetitionUserRole,
  stripPetitionHtml,
} from "utils/petitionHelpers";

export { buildRegionFilterOptions };

const PETITION_TEST_DISTRICT_NAMES = new Set(["ao district 1", "ao district 2"]);

function isAoPetitionRegion(region) {
  const name = String(region?.name || "").trim().toLowerCase();
  const slug = String(region?.slug || "").trim().toLowerCase();
  return name === "ao" || name === "ao region" || slug === "ao" || slug === "ao-region";
}

function isAoPetitionDistrict(district, region) {
  if (isAoPetitionRegion(region)) return true;
  const name = String(district?.name || "").trim().toLowerCase();
  return PETITION_TEST_DISTRICT_NAMES.has(name);
}

/**
 * National petition registry must include AO District 1 (and other AO test districts)
 * so uploaded petitions (e.g. users ofsam, fkwakye) can be reviewed and accepted/rejected.
 * Score / APR dashboards continue using the filtered flattenDistrictsWithRegions.
 * AO districts are included even when org-tree hasAccess is false.
 */
export function flattenDistrictsWithRegionsForPetitions(orgTree) {
  const out = [];

  for (const region of orgTree?.children || []) {
    for (const d of region.children || []) {
      if (d?.level !== "district") continue;

      const includeAoDistrict = isAoPetitionDistrict(d, region);
      if (!includeAoDistrict && d.hasAccess === false) continue;

      const districtId = String(d.orgUnitId ?? d.id ?? "").trim();
      if (!districtId) continue;

      const regionKey = String(region.slug || region.orgUnitId || region.id || region.name || "");
      const slugifyName = (value) =>
        String(value || "")
          .toLowerCase()
          .trim()
          .replace(/&/g, " and ")
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
      const regionSlug = String(region.slug || "").trim() || slugifyName(region.name || regionKey);
      const districtSlug = String(d.slug || "").trim() || slugifyName(d.name);

      out.push({
        districtId,
        districtName: d.name || districtId,
        districtSlug,
        regionKey,
        regionName: region.name || regionKey,
        regionSlug,
      });
    }
  }

  return out;
}

/** @deprecated Prefer flattenDistrictsWithRegionsForPetitions for petition pages. */
export { flattenDistrictsWithRegions };

const BATCH_SIZE = 8;

export const PETITION_STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending review" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
  { value: "submitted", label: "Any petition submitted" },
  { value: "none", label: "Eligible — no petition" },
  { value: "not_eligible", label: "Not eligible" },
];

function findDistrictPetition(rows, districtId, tableId) {
  const list = Array.isArray(rows) ? rows : [];
  return (
    list.find(
      (row) =>
        row.tableCommented === tableId &&
        String(row.districtId) === String(districtId) &&
        normalizePetitionUserRole(row.userRole) === "DDUSER",
    ) ?? null
  );
}

function findCommitteeResponse(rows, districtId, tableId) {
  const list = Array.isArray(rows) ? rows : [];
  return (
    list.find(
      (row) =>
        row.tableCommented === tableId &&
        String(row.districtId) === String(districtId) &&
        normalizePetitionUserRole(row.userRole) === "DPC",
    ) ?? null
  );
}

async function fetchDistrictPetitionBundle(row, year, signal, assessmentStatus) {
  const districtId = row.districtId;
  const y = String(year);
  const petitionTableId = buildDistrictPetitionTableId(y, districtId);
  const committeeTableId = buildCommitteePetitionTableId(y, districtId);

  let districtPetition = null;
  let committeeResponse = null;

  if (!signal?.aborted && isPetitionEligibleAssessmentStatus(assessmentStatus)) {
    const [petitionResult, committeeResult] = await Promise.allSettled([
      instance.get(`comments/table/${districtId}/${y}/${DPAT_PETITION_TYPE}/${petitionTableId}`, { signal }),
      instance.get(`comments/table/${districtId}/${y}/${DPAT_PETITION_TYPE}/${committeeTableId}`, { signal }),
    ]);

    if (petitionResult.status === "fulfilled") {
      districtPetition = findDistrictPetition(petitionResult.value?.data, districtId, petitionTableId);
    }

    if (committeeResult.status === "fulfilled") {
      committeeResponse = findCommitteeResponse(committeeResult.value?.data, districtId, committeeTableId);
    }
  }

  const petitionStatus = derivePetitionRowStatus({
    assessmentStatus,
    districtPetition,
    committeeResponse,
  });

  return {
    ...row,
    year: y,
    assessmentStatus,
    districtPetition,
    committeeResponse,
    petitionStatus,
    petitionSubmittedAt: districtPetition?.commentDate ?? null,
    hasPetitionText: Boolean(districtPetition && stripPetitionHtml(districtPetition.comments)),
  };
}

export async function fetchDistrictPetitions(districtRows, year, signal, onProgress) {
  const y = String(year);
  const total = districtRows.length;
  const results = [];

  const reportProgress = (loaded) => {
    if (!onProgress || signal?.aborted) return;
    const percent = total === 0 ? 100 : Math.min(100, Math.round((loaded / total) * 100));
    onProgress({ loaded, total, percent });
  };

  reportProgress(0);
  if (total === 0) return results;

  let assessmentsByDistrictId = new Map();
  try {
    const assessmentsRes = await instance.get(`assessments/districts/dpat/${y}/DPAT`, { signal });
    const assessments = Array.isArray(assessmentsRes?.data) ? assessmentsRes.data : [];
    assessmentsByDistrictId = new Map(
      assessments
        .filter((item) => item?.districtId != null)
        .map((item) => [String(item.districtId), item]),
    );
  } catch {
    assessmentsByDistrictId = new Map();
  }

  if (signal?.aborted) return results;

  for (let i = 0; i < districtRows.length; i += BATCH_SIZE) {
    if (signal?.aborted) break;
    const slice = districtRows.slice(i, i + BATCH_SIZE);
    const chunk = await Promise.all(
      slice.map((row) => {
        const assessment = assessmentsByDistrictId.get(String(row.districtId));
        const assessmentStatus = assessment?.status ?? null;
        return fetchDistrictPetitionBundle(row, year, signal, assessmentStatus);
      }),
    );
    results.push(...chunk);
    reportProgress(results.length);
  }

  return results;
}

export function summarizePetitions(rows) {
  const eligible = rows.filter((r) => r.petitionStatus !== "not_eligible");
  const submitted = rows.filter((r) => r.hasPetitionText);
  const pending = rows.filter((r) => r.petitionStatus === "pending");
  const accepted = rows.filter((r) => r.petitionStatus === "accepted");
  const rejected = rows.filter((r) => r.petitionStatus === "rejected");

  const byRegion = {};
  for (const row of submitted) {
    const key = row.regionKey || "unknown";
    if (!byRegion[key]) {
      byRegion[key] = { regionKey: key, regionName: row.regionName, count: 0, pending: 0 };
    }
    byRegion[key].count += 1;
    if (row.petitionStatus === "pending") byRegion[key].pending += 1;
  }

  return {
    totalDistricts: rows.length,
    eligibleCount: eligible.length,
    submittedCount: submitted.length,
    pendingCount: pending.length,
    acceptedCount: accepted.length,
    rejectedCount: rejected.length,
    regionBreakdown: Object.values(byRegion).sort((a, b) => b.count - a.count),
  };
}

export function filterPetitionRows(rows, { regionKey = "all", statusFilter = "all", search = "" }) {
  const q = String(search || "").trim().toLowerCase();

  return rows.filter((row) => {
    if (regionKey !== "all" && row.regionKey !== regionKey) return false;

    if (statusFilter !== "all") {
      if (statusFilter === "submitted") {
        if (!row.hasPetitionText) return false;
      } else if (row.petitionStatus !== statusFilter) {
        return false;
      }
    }

    if (q) {
      const hay = `${row.districtName} ${row.regionName}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }

    return true;
  });
}

export function sortPetitionRows(rows) {
  return [...rows].sort((a, b) => {
    const regionCompare = String(a.regionName).localeCompare(String(b.regionName));
    if (regionCompare !== 0) return regionCompare;

    const statusCompare =
      (getPetitionStatusMeta(a.petitionStatus).sortOrder ?? 0) -
      (getPetitionStatusMeta(b.petitionStatus).sortOrder ?? 0);
    if (statusCompare !== 0) return statusCompare;

    return String(a.districtName).localeCompare(String(b.districtName));
  });
}

export async function submitPetitionDecision({
  districtId,
  year,
  decision,
  notes,
  committeeResponse,
  user,
}) {
  const y = String(year);
  const committeeTableId = buildCommitteePetitionTableId(y, districtId);
  const commentDate = new Date().toISOString().split("T")[0].split("-").map(Number);

  const payload = {
    id: committeeResponse?.id || 0,
    username: user?.user?.username || "",
    fullName: user?.user?.fullName || "",
    userRole: "DPC",
    type: DPAT_PETITION_TYPE,
    districtId,
    year: Number(y),
    tableCommented: committeeTableId,
    comments: buildPetitionDecisionHtml(decision, notes),
    commentDate: committeeResponse?.commentDate || commentDate,
    updateDate: commentDate,
    dddpDataDate: commentDate,
    dddpData: { petitionDecision: decision },
  };

  if (committeeResponse?.id) {
    const response = await instance.put(`comments/${committeeResponse.id}`, payload);
    return response.data;
  }

  const response = await instance.post("comments", payload);
  return response.data;
}
